import { cn } from "cn";
import { type VariantProps, cva } from "class-variance-authority";
import type { HTMLAttributes } from "react";

type TextElement = "h1" | "h2" | "h3" | "h4" | "p" | ("span" & {});

const textVariants = cva("", {
  variants: {
    size: {
      xs: "text-xs text-neutral-600 dark:text-neutral-400",
      sm: "text-sm text-neutral-500 dark:text-neutral-400",
      base: "text-sm text-neutral-500 dark:text-neutral-400",
      lg: "text-lg font-semibold text-neutral-800 dark:text-neutral-200",
      xl: "text-xl font-semibold text-neutral-800 dark:text-neutral-200",
      "2xl": "text-2xl font-medium text-neutral-800 dark:text-neutral-200",
      "3xl":
        "text-3xl font-medium text-balance leading-[1.2] tracking-[-0.333333px] text-neutral-800 dark:text-neutral-200",
      "4xl": "text-4xl font-medium text-balance leading-[1.2]",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

type TextProps<T extends TextElement> = {
  as?: T;
  children: React.ReactNode;
} & HTMLAttributes<HTMLElementTagNameMap[T]> &
  VariantProps<typeof textVariants>;

export function Text<T extends TextElement = "p">({
  as,
  children,
  className,
  size,
  ...props
}: TextProps<T>) {
  const Component = as || "p";

  return (
    <Component
      className={cn("text-pretty", textVariants({ size }), className)}
      {...props}
    >
      {children}
    </Component>
  );
}
